import type { ReactNode } from "react";
import { Search, Sparkles, TrendingUp } from "lucide-react";
import type { SlideDef, SlideProps } from "@/engine/types";
import { GradientText, Reveal, SlideShell, useAccent } from "@/ui";

function SearchBar({ tone, children }: { tone: "head" | "tail"; children: ReactNode }) {
  const [from] = useAccent();
  return (
    <div
      className="flex items-center gap-5 rounded-full border px-8 py-5"
      style={
        tone === "tail"
          ? {
              borderColor: `${from}88`,
              background: "rgba(255,255,255,0.04)",
              boxShadow: `0 0 48px -12px ${from}66`,
            }
          : { borderColor: "rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)" }
      }
    >
      <Search className="h-8 w-8 shrink-0 text-white/50" />
      <span className={`font-sans text-white ${tone === "head" ? "text-4xl" : "text-3xl"}`}>
        {children}
      </span>
    </div>
  );
}

function QueryChip({ dot, children }: { dot: string; children: ReactNode }) {
  return (
    <span className="flex items-center gap-2.5 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xl text-white/80">
      <span className={`h-2.5 w-2.5 rounded-full ${dot}`} />
      {children}
    </span>
  );
}

function LongTailSeoSlide({ active }: SlideProps) {
  return (
    <SlideShell chapter={5} title="Long-tail SEO">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
        <Reveal active={active}>
          <SearchBar tone="head">best running shoes</SearchBar>
          <div className="mt-4 flex flex-wrap gap-3 pl-8">
            <QueryChip dot="bg-accent-amber">huge volume</QueryChip>
            <QueryChip dot="bg-accent-amber">head-on competition</QueryChip>
            <QueryChip dot="bg-accent-amber">vague intent</QueryChip>
          </div>
        </Reveal>

        <Reveal active={active} delay={0.35} className="flex items-center gap-6">
          <span className="h-px flex-1 bg-white/10" />
          <span className="text-xl font-medium uppercase tracking-widest text-white/50">vs</span>
          <span className="h-px flex-1 bg-white/10" />
        </Reveal>

        <Reveal active={active} delay={0.6}>
          <SearchBar tone="tail">
            best running shoes{" "}
            <GradientText className="font-semibold">
              for flat feet weeknight training
            </GradientText>
          </SearchBar>
          <div className="mt-4 flex flex-wrap gap-3 pl-8">
            <QueryChip dot="bg-accent-emerald">lower volume</QueryChip>
            <QueryChip dot="bg-accent-emerald">clearer intent</QueryChip>
            <QueryChip dot="bg-accent-emerald">“easier to answer completely”</QueryChip>
          </div>
        </Reveal>

        <Reveal
          active={active}
          delay={1.1}
          className="mt-8 flex items-start justify-between gap-12 border-t border-white/10 pt-7"
        >
          <p className="flex items-start gap-4 text-2xl leading-relaxed text-white/80">
            <Sparkles className="mt-1 h-7 w-7 shrink-0 text-accent-violet" />
            <span>
              Great for{" "}
              <span className="font-semibold text-white">
                niche products, how-tos, and regional phrasing
              </span>
              .
            </span>
          </p>
          <p className="flex items-start gap-4 text-2xl leading-relaxed text-white/80">
            <TrendingUp className="mt-1 h-7 w-7 shrink-0 text-accent-emerald" />
            <span>
              Many small pages add up in{" "}
              <span className="font-semibold text-white">aggregate clicks</span>.
            </span>
          </p>
        </Reveal>
      </div>
    </SlideShell>
  );
}

export const longTailSeo: SlideDef = {
  id: "c5-longtail",
  chapter: 5,
  title: "Long-tail SEO",
  component: LongTailSeoSlide,
};
